import { useState } from 'react';

import { Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';

import { hasClientRole } from '@/auth/roles';
import { useTenantStore } from '@/auth/tenantStore';
import { useTenantTheme } from '@/auth/useTenantTheme';
import { useAuth } from '@/auth/useAuth';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AccessDenied } from '@/components/layout/AccessDenied';
import { BreadcrumbsProvider } from '@/components/layout/breadcrumbs';
import { NoTenantAccess } from '@/components/layout/NoTenantAccess';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { ModalProvider } from '@/components/modal/ModalProvider';
import { BulkJobsListener } from '@/realtime/BulkJobsListener';

function RootComponent() {
  const { loading, authenticated, accessDenied } = useAuth();
  const me = useTenantStore((s) => s.me);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Recolor the app shell to the active tenant's accent (no-op for the default).
  useTenantTheme();

  if (loading) return <LoadingSpinner fullPage message="Initializing..." />;
  if (!authenticated) return <LoadingSpinner fullPage message="Redirecting to login..." />;
  if (accessDenied) return <AccessDenied />;
  if (me && !hasClientRole(me.clientRole)) return <AccessDenied />;

  // After /me hydrates: a user with no active membership in any tenant gets a
  // dedicated screen instead of the regular shell, which would otherwise issue
  // tenanted requests that all 400/403. Super-admins land here only when no tenant
  // exists yet — but they must still reach /admin to create one, so admin routes
  // are exempt: those endpoints send X-Tenant-Id:'*' and the shell's tenant-scoped
  // listeners self-guard on currentTenantId. Subscribing to `me` here means a
  // later /me refetch (e.g. after a server-side deactivate) flips in without
  // reload.
  if (
    me &&
    me.memberships.every((m) => m.status !== 'active') &&
    !(me.isSuperAdmin && pathname.startsWith('/admin'))
  ) {
    return (
      <ModalProvider>
        <NoTenantAccess isSuperAdmin={me.isSuperAdmin} />
      </ModalProvider>
    );
  }

  return (
    <ModalProvider>
      <BreadcrumbsProvider>
        {/* Realtime job updates (SignalR + polling fallback) for the active tenant. */}
        <BulkJobsListener />
        <div className="app-shell">
          <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
          <main className="app-main">
            <TopBar onMenuClick={() => setMobileOpen((prev) => !prev)} />
            <div className="app-page">
              <Outlet />
            </div>
          </main>
        </div>
      </BreadcrumbsProvider>
    </ModalProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: ErrorComponent,
});
